import type { VariantProps } from 'class-variance-authority';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { type ComponentProps } from 'react';

import { Separator } from '@/components/ui/separator';

export const buttonGroupVariants = cva(
  "has-[>[data-slot=button-group]]:gap-2 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md flex w-fit items-stretch *:focus-visible:z-10 *:focus-visible:relative [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal:
          '[&>[data-slot]:not(:has(~[data-slot]))]:rounded-r-md! [&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:first-child):focus-visible]:[box-shadow:inset_1px_0_0_0_var(--color-border-primary-focus)] [&>*:not(:last-child)]:rounded-r-none',
        vertical:
          '[&>[data-slot]:not(:has(~[data-slot]))]:rounded-b-md! flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:first-child):focus-visible]:[box-shadow:inset_0_1px_0_0_var(--color-border-primary-focus)] [&>*:not(:last-child)]:rounded-b-none',
      },
    },
    defaultVariants: {
      orientation: 'horizontal',
    },
  }
);

export function ButtonGroup(buttonGroupProps: ComponentProps<'div'> & VariantProps<typeof buttonGroupVariants>) {
  const { className, orientation, ...props } = buttonGroupProps;
  return <div role="group" data-slot="button-group" data-orientation={orientation} className={cn(buttonGroupVariants({ orientation }), className)} {...props} />;
}

export function ButtonGroupText(
  buttonGroupTextProps: ComponentProps<'div'> & {
    asChild?: boolean;
  }
) {
  const { className, asChild = false, ...props } = buttonGroupTextProps;
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp className={cn("bg-muted gap-2 rounded-md border px-2.5 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none", className)} {...props} />
  );
}

export function ButtonGroupSeparator(buttonGroupSeparatorProps: ComponentProps<typeof Separator>) {
  const { className, orientation = 'vertical', ...props } = buttonGroupSeparatorProps;
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={cn('bg-input relative self-stretch data-horizontal:mx-px data-horizontal:w-auto data-vertical:my-px data-vertical:h-auto', className)}
      {...props}
    />
  );
}
